/* eslint-disable react/require-default-props */
import * as React from 'react';
import { useMutation } from '@apollo/client';
import { capitalize } from 'lodash';
import { useForm } from 'react-hook-form';

import {
  Button,
  Drawer,
  Loader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
} from '@teamsnap/teamsnap-ui';
import { Organization } from 'models';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { AccountStatus } from 'components/organization/organization-accounts-management';
import FormElementHook from './form-element-hook';
import PaymentAccount from 'models/payment-account';

import * as CreatePaymentAccount from 'core/gql/mutations/create-payment-account';
import * as UpdatePaymentAccount from 'core/gql/mutations/update-payment-account';
import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import * as CreateOnboardingLink from 'core/gql/mutations/create-onboarding-link';

import './create-account-drawer.scss';

export enum AUTOCOMPLETE {
  ON = 'on',
  OFF = 'off',
}

export interface Props {
  setAccount?: React.Dispatch<any>;
  account?: PaymentAccount;
  organization: Organization;
  isDrawOpen: boolean;
  onDrawClose: () => void;
  createEditCallback?: (paymentAccount: PaymentAccount) => void;
  redirectUrl?: string;
}

const CreateElement = FormElementHook as new () => FormElementHook;

const CreateAccountDrawer: React.FunctionComponent<Props> = ({
  setAccount,
  isDrawOpen,
  onDrawClose,
  organization,
  account,
  createEditCallback,
  redirectUrl,
}) => {
  const [stripeError, setStripeError] = React.useState<string | null>(null);
  const [showRedirectMessage, setShowRedirectMessage] = React.useState(false);
  const accountForm = useForm();
  const autoComplete: string = AUTOCOMPLETE.OFF;

  const isValidDescriptor = (value: string) => {
    const regex = /^[a-zA-Z0-9 ]*$/;
    const isAllLatinCharacters = regex.test(value);
    const containsOneLetter = value.match(/[a-zA-Z]/g)?.length >= 1;

    return isAllLatinCharacters && containsOneLetter;
  };

  const [createOnboardingLink, createOnboardingLinkResponse] = useMutation(
    CreateOnboardingLink.MUTATION
  );

  const IS_INDIVIDUAL = React.useCallback(
    () =>
      account?.businessType === 'individual' ||
      (!account && organization?.businessType === 'individual'),
    [account, organization]
  );

  const IS_APPROVED = React.useCallback(
    () =>
      account &&
      (account.status === AccountStatus.APPROVED ||
        account.status === AccountStatus.RESTRICTED_SOON),
    [account]
  );

  const IS_NON_EDITABLE = React.useCallback(
    () =>
      account &&
      (account.status === AccountStatus.UNDER_REVIEW ||
        account.status === AccountStatus.INFO_NEEDED ||
        account.status === AccountStatus.REJECTED),
    [account]
  );

  const displayBusinessType = (account?: PaymentAccount) => {
    const businessType = account?.businessType ?? organization?.businessType;

    if (businessType.includes('-')) {
      return businessType
        .split('-')
        .map((i) => i[0].toUpperCase() + i.slice(1))
        .join('-');
    }

    return capitalize(businessType);
  };

  const handleNewAccount = () => {
    setAccount(null);
  };

  React.useEffect(() => {
    const values = accountForm.getValues();
    const getNickname = (account: PaymentAccount) =>
      values?.nickname && values?.nickname !== ''
        ? values.nickname
        : account.nickname;

    const getPayoutDescriptor = (account: PaymentAccount) =>
      values?.payout_descriptor && values?.payout_descriptor !== ''
        ? values.payout_descriptor
        : account.payoutDescriptor;

    const setFormDefaults = (account: PaymentAccount) => {
      const formValues = {
        id: account.id,
      };

      if (IS_INDIVIDUAL()) {
        formValues['first_name'] = account?.firstName;
        formValues['last_name'] = account?.lastName;
      } else {
        formValues['legal_entity_name'] = account?.legalEntityName;
      }

      if (account.nickname) formValues['nickname'] = getNickname(account);
      if (account.payoutDescriptor)
        formValues['payout_descriptor'] = getPayoutDescriptor(account);
      if (account.bankAccountNumberLast4)
        formValues['account_number'] = account.bankAccountNumberLast4;

      if (account.bankRoutingNumber) {
        if (account.country === 'US')
          formValues['routing_number'] = account.bankRoutingNumber;
        if (account.country === 'CA') {
          const v = account.bankRoutingNumber?.split('-');

          formValues['transit_number'] = v?.[0];
          formValues['institution_number'] = v?.[1];
        }
      }

      Object.entries(formValues).forEach(([k, v]) => {
        accountForm.setValue(k, v);
      });
    };

    if (account && Object.keys(accountForm.errors).length === 0) {
      setFormDefaults(account);
    }
  }, [IS_INDIVIDUAL, account, accountForm]);

  const { handleSubmit, register } = accountForm;

  const [createPaymentAccount, createPaymentAccountResponse] = useMutation(
    CreatePaymentAccount.MUTATION,
    {
      refetchQueries: [
        {
          query: ListPaymentAccounts.QUERY,
          variables: {
            scopeId: +organization?.id,
            scopeType: 'organization',
          },
        },
      ],
    }
  );

  const [updatePaymentAccount, updatePaymentAccountResponse] = useMutation(
    UpdatePaymentAccount.MUTATION,
    {
      refetchQueries: [
        {
          query: ListPaymentAccounts.QUERY,
          variables: { scopeId: +organization?.id, scopeType: 'organization' },
        },
      ],
    }
  );

  const isSubmitting =
    createPaymentAccountResponse.loading ||
    updatePaymentAccountResponse.loading ||
    createOnboardingLinkResponse.loading;

  const submit = async (values) => {
    const obj = account ?? organization;

    const getCountry = (country: string) => {
      switch (country) {
        case 'United States':
          return 'US';
        case 'Canada':
          return 'CA';
        default:
          return '';
      }
    };

    const variables = {
      businessType:
        obj.businessType === 'corporation'
          ? 'company'
          : obj.businessType.replace('-', '_'),
      country: getCountry(obj.country),
      currency: obj.currency,
      nickname: values.nickname,
      payoutDescriptor: values.payout_descriptor,
    };

    const handleClose = () => {
      onDrawClose();
      setAccount(null);
    };

    if (IS_INDIVIDUAL()) {
      variables['firstName'] = values.first_name;
      variables['lastName'] = values.last_name;
    } else {
      variables['legalEntityName'] = values.legal_entity_name;
    }

    if (!account?.id) {
      variables['scopeId'] = organization.id;
      variables['scopeType'] = 'organization';
      variables['processor'] = organization.paymentProvider;
    }

    const mutation = account?.id ? updatePaymentAccount : createPaymentAccount;

    if (account && account.id) {
      variables['legalEntityName'] = account?.legalEntityName;
      variables['id'] = account.id;
    } else {
      variables['isMerchant'] = true;
    }

    let mutationResponse;

    try {
      const response = await mutation({ variables });
      mutationResponse = {
        ...response.data?.createPaymentAccount,
        ...response.data?.updatePaymentAccount,
      };

      if (createEditCallback) {
        createEditCallback(mutationResponse);
      }

      reactiveFeedbackMessage({
        message: `Merchant account "${values.nickname}" has been ${
          account?.id ? 'updated' : 'created'
        }.`,
        isError: false,
        autoclose: true,
      });

      accountForm.reset();

      if (redirectUrl && !account?.id) {
        handleVerifyAccount(mutationResponse);
        setShowRedirectMessage(true);
      } else {
        handleClose();
      }
    } catch (e) {
      console.error(e);
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });

      accountForm.reset();
      handleClose();
    }
  };

  const renderLegalName = () => {
    if (IS_INDIVIDUAL()) {
      return (
        <>
          <div className="u-padXs u-spaceEndsXs">
            <CreateElement
              disabled={isSubmitting || !!account?.id || IS_NON_EDITABLE()}
              inputRef={register({
                required: 'You must enter your first name.',
              })}
              useHookResponse={accountForm}
              type="text"
              field="first_name"
              label={
                <>
                  First Name
                  <span className="u-colorNegative">*</span>
                </>
              }
              placeholder="John"
            />
          </div>

          <div className="u-padXs u-spaceEndsXs">
            <CreateElement
              disabled={isSubmitting || !!account?.id || IS_NON_EDITABLE()}
              inputRef={register({
                required: 'You must enter your last name.',
              })}
              useHookResponse={accountForm}
              type="text"
              field="last_name"
              label={
                <>
                  Last Name
                  <span className="u-colorNegative">*</span>
                </>
              }
              placeholder="Doe"
            />
          </div>
        </>
      );
    } else if (account?.id) {
      return (
        <div className="u-flex u-flexJustifyBetween u-padXs">
          <span>Legal Entity Name</span>
          <span>
            {account?.legalEntityName && capitalize(account?.legalEntityName)}
          </span>
        </div>
      );
    } else {
      return (
        <div className="u-padXs u-spaceEndsXs">
          <CreateElement
            disabled={isSubmitting || !!account?.id || IS_NON_EDITABLE()}
            inputRef={register({
              required: 'You must enter your legal entity name.',
            })}
            useHookResponse={accountForm}
            type="text"
            field="legal_entity_name"
            label={
              <>
                Legal Entity Name
                <span className="u-colorNegative">*</span>
              </>
            }
            placeholder="Organization Name"
          />
        </div>
      );
    }
  };

  const handleVerifyAccount = async (item: PaymentAccount) => {
    const variables = {
      id: item.id,
      returnUrl: redirectUrl,
      refreshUrl: redirectUrl,
      isMerchant: true,
      type: 'account_onboarding',
    };

    const response = await createOnboardingLink({ variables });

    if (response) {
      const url = response.data?.createOnboardingLink?.url;

      if (url) {
        window.location.href = url;
      }
    }
  };

  return (
    <div id="merchant-account-drawer">
      {isDrawOpen && (
        <Drawer
          open={true}
          allowOverlayClose={false}
          closeFn={() => onDrawClose()}
          style={{ maxWidth: '480px' }}
          mods="u-size12of24"
          showOverlay
        >
          {!showRedirectMessage ? (
            <Panel
              mods="Panel CreateAccountDrawer"
              className="u-padSidesMd registration-share-form"
            >
              <PanelHeader>
                <h3
                  className="u-spaceTopNone"
                  data-testid="create-merchant__account-drawer--container-header"
                >
                  {account?.id
                    ? 'Edit Merchant Account'
                    : 'Create Merchant Account'}
                </h3>
                <div className="Modal-close">
                  <Button
                    isDisabled={isSubmitting}
                    onClick={() => {
                      onDrawClose();
                    }}
                    icon="dismiss"
                    mods="u-colorNeutral9"
                    style={{ border: 'none' }}
                  />
                </div>
              </PanelHeader>

              <PanelBody>
                <form data-testid="CreateAccountDrawer--form">
                  <h4 className="u-spaceTopMd">Merchant Account Information</h4>
                  <Panel mods="u-spaceTopMd u-padMd">
                    <div className="u-flex u-flexJustifyBetween u-padXs">
                      <span>Country</span>
                      <span data-testid="CreateAccountDrawer--country">
                        {organization?.country}
                      </span>
                    </div>

                    <div className="u-flex u-flexJustifyBetween u-padXs">
                      <span>Business Type</span>
                      <span data-testid="CreateAccountDrawer--business-type">
                        {displayBusinessType(account)}
                      </span>
                    </div>

                    {renderLegalName()}

                    <div className="u-padXs u-spaceEndsXs">
                      <CreateElement
                        disabled={isSubmitting || IS_NON_EDITABLE()}
                        inputRef={register({
                          required:
                            'You must enter a merchant account nickname.',
                        })}
                        useHookResponse={accountForm}
                        type="text"
                        field="nickname"
                        label={
                          <>
                            Merchant Account Nickname
                            <span className="u-colorNegative">*</span>
                          </>
                        }
                        placeholder="Account Nickname"
                      />
                    </div>

                    <div className="u-padXs u-spaceEndsXs">
                      <CreateElement
                        disabled={isSubmitting || IS_NON_EDITABLE()}
                        inputRef={register({
                          required: 'You must enter a payout descriptor.',
                          maxLength: {
                            value: 22,
                            message:
                              'Must be between 5 and 22 Latin characters, containing at least one letter.',
                          },
                          minLength: {
                            value: 5,
                            message:
                              'Must be between 5 and 22 Latin characters, containing at least one letter.',
                          },
                          validate: (value) =>
                            isValidDescriptor(value) ||
                            'Must be between 5 and 22 Latin characters, containing at least one letter.',
                        })}
                        useHookResponse={accountForm}
                        type="text"
                        field="payout_descriptor"
                        label={
                          <>
                            Payout Descriptor
                            <span className="u-colorNegative">*</span>
                          </>
                        }
                        placeholder="Text that appears on a bank account statement for payouts"
                      />
                    </div>
                  </Panel>

                  {IS_APPROVED() && (
                    <>
                      <h4 className="u-spaceTopLg">Connected Bank Account</h4>

                      <Panel mods="u-spaceTopMd u-padMd">
                        <h4 className="u-spaceXs u-spaceBottomXs u-borderBottom u-padBottomXs u-borderColorDefault u-inlineBlock u-spaceTopMd">
                          Bank Account Information
                        </h4>

                        {stripeError && (
                          <p className="validation-error u-spaceTopSm">
                            {stripeError}
                          </p>
                        )}

                        <div className="u-padXs u-spaceEndsXs">
                          <CreateElement
                            disabled={true}
                            onChange={() => setStripeError(null)}
                            inputRef={register({
                              required: 'You must enter the account number.',
                            })}
                            useHookResponse={accountForm}
                            type="text"
                            field="account_number"
                            hasExternalError={stripeError ? true : false}
                            label={
                              <>
                                Account Number
                                <span className="u-colorNegative">*</span>
                              </>
                            }
                            placeholder="000123456789"
                            autoComplete={autoComplete}
                          />
                        </div>

                        {organization?.country === 'United States' && (
                          <div className="u-padXs u-spaceEndsXs">
                            <CreateElement
                              disabled={true}
                              onChange={() => setStripeError(null)}
                              inputRef={register()}
                              useHookResponse={accountForm}
                              type="text"
                              field="routing_number"
                              hasExternalError={stripeError ? true : false}
                              label={
                                <>
                                  Routing Number
                                  <span className="u-colorNegative">*</span>
                                </>
                              }
                              placeholder="111000000"
                              autoComplete={autoComplete}
                            />
                          </div>
                        )}

                        {organization?.country === 'Canada' && (
                          <div className="u-flex">
                            <div className="u-padXs u-spaceEndsXs">
                              <CreateElement
                                disabled={true}
                                inputRef={register()}
                                useHookResponse={accountForm}
                                type="text"
                                hasExternalError={stripeError ? true : false}
                                field="transit_number"
                                label={
                                  <>
                                    Transit Number
                                    <span className="u-colorNegative">*</span>
                                  </>
                                }
                                placeholder="12345"
                              />
                            </div>

                            <div className="u-padXs u-spaceEndsXs">
                              <CreateElement
                                disabled={true}
                                inputRef={register()}
                                useHookResponse={accountForm}
                                type="text"
                                hasExternalError={stripeError ? true : false}
                                field="institution_number"
                                label={
                                  <>
                                    Institution Number
                                    <span className="u-colorNegative">*</span>
                                  </>
                                }
                                placeholder="678"
                              />
                            </div>
                          </div>
                        )}
                      </Panel>
                    </>
                  )}

                  {(IS_APPROVED() || IS_NON_EDITABLE()) && (
                    <Button
                      onClick={handleNewAccount}
                      type="link"
                      mods="u-colorInfo u-textSemiBold"
                    >
                      To use a new bank account, please{' '}
                      <span className="u-colorPrimary">create</span> a new
                      merchant account.
                    </Button>
                  )}

                  <PanelFooter>
                    <div className="button-list">
                      <Button
                        isDisabled={isSubmitting}
                        onClick={() => {
                          onDrawClose();
                        }}
                      >
                        Cancel
                      </Button>

                      {!IS_NON_EDITABLE() && (
                        <Button
                          isDisabled={isSubmitting}
                          type="submit"
                          color="primary"
                          onClick={(e) => {
                            e.preventDefault();
                            setStripeError(null);
                            handleSubmit(submit)();
                          }}
                          testId="create-merchant-account-drawer--submit-button"
                        >
                          {account?.id ? 'Save' : 'Create'}
                        </Button>
                      )}
                    </div>
                  </PanelFooter>
                </form>
              </PanelBody>
            </Panel>
          ) : (
            <div className="sui-flex sui-flex-col sui-justify-center sui-items-center sui-text-center sui-h-[90vh]">
              <Loader size={{ height: 24, width: 24 }} />
              <div className="sui-block sui-heading-sm sui-mt-1">
                Redirecting to Stripe
              </div>
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
};

export default CreateAccountDrawer;
